// MODIFIED: Ensuring helper buttons do not trigger form submission
import React, { useState, useRef, useEffect } from "react";
import { Input, TextArea } from "../../../../common/Input";
import { Select } from "../../../../common/Select";
import { Button, GeneralMessage } from "../../../../common/APICommon";
import { ConfirmationModal } from "../../../../common/Modal";
import AddIcon from '@mui/icons-material/Add';
import { EmployeePickerField } from "../../../../common/selectComponents/EmployeePicker";
import DTRItineraryTable from "./components/DTRItineraryTable";
import DTRItineraryAddEditModal from "./components/DTRItineraryAddEditModal";

const DTRCorrectionModule = ({
  user, userAccess, itineraryList, setItineraryList,
  formData, handleInputChange, handleFileChange, submitRequest,
  showMessage, message, isSuccess, loading
}) => {
  const [canFullView, setCanFullView] = useState(false);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isItineraryModalOpen, setIsItineraryModalOpen] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [itineraryPage, setItineraryPage] = useState(1);
  const [errorState, setErrorState] = useState(false);

  const fileInputRef = useRef(null);

  const timeTypeOptions = [
    { value: "In", label: "Time-In" },
    { value: "Out", label: "Time-Out" },
    { value: "NoInOut", label: "No In and No Out" }
  ];

  const onFormSubmit = (e) => {
    if (e) e.preventDefault(); // This should only run when the actual BLUE button is clicked

    const isNoInOut = formData.timeType === "NoInOut";
    const hasRequiredBaseFields = formData.employeeId && formData.date && formData.timeType && formData.reason;
    const hasRequiredTimeFields = isNoInOut
      ? (formData.timeRenderedNoIn && formData.timeRenderedNoOut)
      : formData.timeRendered;

    if (!hasRequiredBaseFields || !hasRequiredTimeFields) {
      setErrorState(true);
      return;
    }

    setErrorState(false);
    setIsConfirmOpen(true);
  };

  useEffect(() => {
    setCanFullView(userAccess?.isFullTransactionAccess);
  }, [userAccess]);

  return (
    <>
      <div className="p-4 space-y-6">
        <div className="bg-white rounded-lg p-1">
          <form onSubmit={onFormSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">

            <div className="space-y-4">
              <EmployeePickerField
                label="Employee"
                isRequired={true}
                employeePersonalInformationId={formData.employeeId}
                displayValue={formData.employeeName}
                onSelect={(emp) => {
                  handleInputChange('employeeId', emp.id);
                  handleInputChange('employeeName', emp.fullName);
                }}
                onClear={() => {
                  handleInputChange('employeeId', '');
                  handleInputChange('employeeName', '');
                }}
                error={errorState && !formData.employeeId}
                errorMessage="Employee is required"
                viewMode={!canFullView}
              />

              <Select
                label="Time Type"
                options={timeTypeOptions}
                value={formData.timeType}
                onChange={(e) => handleInputChange('timeType', e.target.value)}
                isRequired
                error={errorState && !formData.timeType}
              />

              <div className="flex flex-col">
                <label className="text-sm font-bold text-gray-600 mb-1">Date of Correction *</label>
                <input
                  type="date"
                  className={`p-2 border rounded-md outline-none ${errorState && !formData.date ? 'border-red-500' : 'border-gray-300'}`}
                  value={formData.date ? formData.date.split('T')[0] : ""}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                />
              </div>

              {formData.timeType === "NoInOut" ? (
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Time In" type="time" value={formData.timeRenderedNoIn} onChange={(e) => handleInputChange('timeRenderedNoIn', e.target.value)} error={errorState && !formData.timeRenderedNoIn} />
                  <Input label="Time Out" type="time" value={formData.timeRenderedNoOut} onChange={(e) => handleInputChange('timeRenderedNoOut', e.target.value)} error={errorState && !formData.timeRenderedNoOut} />
                </div>
              ) : (
                <Input label="Time Requested" type="time" value={formData.timeRendered} onChange={(e) => handleInputChange('timeRendered', e.target.value)} error={errorState && formData.timeType && !formData.timeRendered} />
              )}
            </div>

            <div className="flex flex-col space-y-4">
              <TextArea
                label="Reason"
                placeholder="Enter reason for correction..."
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                rows={6}
                error={errorState && !formData.reason}
                isRequired
              />

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-gray-50">
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={(e) => handleFileChange(e.target.files[0])} />
                <p className="text-xs text-gray-500 mb-2">{formData.fileName || "Upload Image"}</p>
                <div className="flex justify-center gap-2">
                  <Button
                    type="button"
                    name="Upload File"
                    className="bg-gray-600 text-white text-xs"
                    onClick={() => fileInputRef.current.click()}
                  />
                </div>
              </div>
            </div>

            <div className="md:col-span-2 mt-4 pt-6 border-t border-gray-100">
              <div className="flex justify-between items-center ">
                <h4 className="text-lg font-bold text-customMainColor">Itinerary Records</h4>
                <Button
                  type="button"
                  name="Add Itinerary"
                  icon={<AddIcon />}
                  className="bg-customGreen text-white text-xs border-none"
                  onClick={() => { setSelectedItinerary(null); setIsItineraryModalOpen(true); }}
                />
              </div>
              <DTRItineraryTable data={itineraryList} onEdit={(item) => { setSelectedItinerary(item); setIsItineraryModalOpen(true); }} onDelete={(item) => setItineraryList(prev => prev.filter(i => i.id !== item.id))} currentPage={itineraryPage} setCurrentPage={setItineraryPage} />
            </div>

            <div className="md:col-span-2 flex justify-end">
              <Button
                type="submit" // ONLY this button should be type submit
                name="SEND DTR REQUEST"
                className="bg-blue-600 text-white px-12 py-3 font-bold"
              />
            </div>
          </form>
        </div>

      </div>
      <DTRItineraryAddEditModal
        isOpen={isItineraryModalOpen}
        onClose={() => setIsItineraryModalOpen(false)}
        selectedItem={selectedItinerary}
        onSave={(form) => {
          if (selectedItinerary)
            setItineraryList(prev => prev.map(item => item.id === selectedItinerary.id
              ? { ...form, id: selectedItinerary.id }
              : item));
          else
            setItineraryList(prev => [...prev, { ...form, id: Date.now() }]);
          setIsItineraryModalOpen(false);
        }}
      />

      <ConfirmationModal
        visible={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={async () => {
          const success = await submitRequest();
          if (success)
            setIsConfirmOpen(false);
        }
        }
        title="Confirm DTR Correction"
        message={`Submit correction request?`}
        isLoading={loading}
      />
      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
    </>
  );
};

export default DTRCorrectionModule;